import { isAllowedLlmUpstreamUrl, isBlockedLlmUpstreamUrl } from "../../lib/llmAllowedHosts";
import { comboKey, FEATURE_LABELS, type EndpointCombo, type FeatureId, type FeatureRouteMap } from "../../services/featureRoutes";

export const EVOLUTION_ROUTE_IDS = ["evolution-entry", "evolution-item", "evolution-player", "evolution-npc", "evolution-monster", "evolution-misc"] as const satisfies readonly FeatureId[];

export interface ReadinessIssue {
  feature: FeatureId;
  label: string;
  panel: "api-config" | "feature-routes";
  message: string;
}

function isUsableEndpointUrl(combo: EndpointCombo) {
  if (combo.useProxy) return isAllowedLlmUpstreamUrl(combo.baseUrl);
  try {
    const url = new URL(combo.baseUrl);
    return (url.protocol === "https:" || url.protocol === "http:") && !isBlockedLlmUpstreamUrl(combo.baseUrl);
  } catch {
    return false;
  }
}

export function isUsableCombo(combo: EndpointCombo | undefined, kind: EndpointCombo["kind"] = "generation") {
  return Boolean(combo?.enabled && combo.kind === kind && combo.model.trim() && isUsableEndpointUrl(combo) && comboKey(combo.id).trim());
}

export function routeIsUsable(feature: FeatureId, combos: EndpointCombo[], routes: FeatureRouteMap) {
  const byId = new Map(combos.map((combo) => [combo.id, combo]));
  const kind = feature === "rag-embedding" ? "embedding" : "generation";
  return (routes[feature] ?? []).some((id) => isUsableCombo(byId.get(id), kind));
}

export function validateGameStartReadiness(input: { endpointCombos: EndpointCombo[]; featureRoutes: FeatureRouteMap; evolutionEnabled?: boolean; evolutionRoutesSplit?: boolean }): ReadinessIssue[] {
  const evolutionRequired: FeatureId[] = input.evolutionEnabled === false ? [] : input.evolutionRoutesSplit === false ? ["evolution"] : [...EVOLUTION_ROUTE_IDS];
  const required: FeatureId[] = ["core-chat", ...evolutionRequired];
  return required.flatMap((feature) => {
    if (routeIsUsable(feature, input.endpointCombos, input.featureRoutes)) return [];
    const assigned = input.featureRoutes[feature] ?? [];
    return [{
      feature,
      label: FEATURE_LABELS[feature],
      panel: assigned.length ? "api-config" : "feature-routes",
      message: assigned.length ? "已分配接口，但接口未启用、地址/模型无效，或当前会话尚未填写 API Key。" : "尚未分配可用接口。",
    } satisfies ReadinessIssue];
  });
}

export function hasUsableGenerationEndpoint(combos: EndpointCombo[]) {
  return combos.some((combo) => isUsableCombo(combo));
}
